/**
 * mergeAnalysis — fold an `/api/v2/analyze-stack` response back onto the
 * member's own supplement rows, and derive what the muted slot should say.
 *
 * Pure, and in its own module rather than inside `StackForm`, because this is
 * the function STACK-544's named unattended risk lives in: *"if the member's
 * typed dose ever renders in the study chip, a member reads their own number
 * as study-backed"*. The inverse is just as bad and is the one a spread
 * operator produces by accident — an analysis field called `dosage` landing on
 * top of the amount the member typed.
 */

/**
 * The only fields an analysis response may contribute to a member's row.
 *
 * **An allowlist, not a blocklist, and that is the whole guard.** The obvious
 * implementation is `{ ...memberRow, ...analysisRow }`, which silently lets any
 * present or future response field overwrite a member's own data — `dosage` and
 * `unit` first among them. With an allowlist, a response that carries a rogue
 * `dosage` cannot reach the row no matter what the backend sends, and the test
 * that proves it sends exactly that.
 *
 * Shape reference: `backend/endpoints/analyze_stack.py` (PR 345).
 */
export const ANALYSIS_FIELDS = Object.freeze([
  'evidenceScore',
  'study_count',
  'total_included_population',
  'studyDose',
  'entryState',
  'doseComparison',
  'merged',
  'mergedEntries',
  'unitConflict',
]);

/** Entry states the backend can report. `ok` is the clean case. */
export const ENTRY_STATES = Object.freeze({
  OK: 'ok',
  ADD_AMOUNT: 'add_amount',
  CHECK_AMOUNT: 'check_amount',
  NOT_IN_LIBRARY: 'not_in_library',
});

/**
 * Attach analysis results to the member's rows, matched by `supplementId`.
 *
 * Rows the response does not mention come back untouched — the member may have
 * added a supplement after the analysis started, and a row with no analysis is
 * simply a row with no analysis, not an error.
 *
 * **A supplement entered more than once becomes one row showing the combined
 * amount** (PRD: "If someone enters vitamin D twice, the row shows the
 * combined amount"; Tanu, 2026-09-24). The backend has already summed those
 * rows — the member's own amounts, including the IU↔mcg conversion for
 * vitamin D — and returns the total on a response row flagged `merged`. The
 * first of the member's rows takes that total and the later ones fold into
 * it. Nothing typed is lost: `mergedEntries` keeps every original row.
 *
 * Three cases fold nothing and keep the member's rows as they are:
 * - `unitConflict` — the backend could not add the units (mg + g of a
 *   non-vitamin-D supplement), so there is no honest total to show;
 * - a row the member switched off (`selected === false`) — it was never sent,
 *   so it is not part of the backend's sum;
 * - a response row with no total on it, rather than invent one.
 *
 * @param {Array<object>} supplements the member's rows, in their order
 * @param {object|null} analysis the endpoint's result payload
 * @returns {Array<object>} new rows; the input is not mutated
 */
export function mergeAnalysis(supplements, analysis) {
  const rows = Array.isArray(analysis?.supplements) ? analysis.supplements : [];
  const byId = new Map();
  rows.forEach((row) => {
    if (row && row.supplementId) byId.set(idKey(row.supplementId), row);
  });

  const folded = new Set();
  const sentSoFar = new Map(); // id -> how many of this id's sent rows came before
  const out = [];
  (supplements || []).forEach((supplement) => {
    const key = supplement && idKey(supplement.supplementId);
    const row = supplement && byId.get(key);
    if (!row) {
      out.push(supplement);
      return;
    }
    // Position among this supplement's rows that were sent: the backend keeps
    // `mergedEntries` in the order it received them.
    const sentIndex = supplement.selected !== false ? (sentSoFar.get(key) || 0) : -1;
    if (sentIndex >= 0) sentSoFar.set(key, sentIndex + 1);

    const combines = row.merged === true
      && !row.unitConflict
      && row.dosage != null
      && supplement.selected !== false;
    if (combines && folded.has(key)) return; // folded into the first
    if (combines) folded.add(key);

    const analysisFields = {};
    ANALYSIS_FIELDS.forEach((field) => {
      if (row[field] !== undefined) analysisFields[field] = row[field];
    });

    // A row this form already combined stays combined when the analysis is run
    // again. The re-run sees one row carrying the total, so it reports
    // `merged: false`; without this the "Amounts are combined" line vanished
    // on the second Analyze although nothing about the row had changed
    // (Tanu, 2026-09-24). A dose edit is what ends it — see `afterDoseEdit`.
    if (supplement.merged === true && row.merged !== true) {
      analysisFields.merged = true;
      if (supplement.mergedEntries !== undefined) analysisFields.mergedEntries = supplement.mergedEntries;
    }

    // Duplicates that could not be added (300 mg and 2 capsules) stay separate
    // rows, but the backend judged them as one: the first amount, so "ok". The
    // capsule row then showed no Add amount and sat beside the studied dose
    // (Tanu's staging test, cases D6/E1). The backend now also judges each
    // entry; each row takes its own. Without per-entry states (an older
    // backend) nothing changes.
    if (row.unitConflict && sentIndex >= 0) {
      const own = Array.isArray(row.mergedEntries) ? row.mergedEntries[sentIndex] : null;
      if (own && typeof own.entryState === 'string') analysisFields.entryState = own.entryState;
    }

    // No studies means no score, not a score of zero. The backend reports
    // `evidenceScore: 0` for a supplement it found nothing on, and the ring
    // would draw that as "0.0" — a claim of zero relevance about something the
    // research simply did not cover. `null` draws the unscored ring instead.
    if (row.study_count === 0) analysisFields.evidenceScore = null;

    // The member's row is spread LAST for `dosage`/`unit` specifically, so even
    // a future edit that widened ANALYSIS_FIELDS by mistake cannot move the
    // number the member typed. The one exception is a combined row, whose
    // amount is the sum of the member's own entries, read deliberately here.
    out.push({
      ...supplement,
      ...analysisFields,
      dosage: combines ? String(row.dosage) : supplement.dosage,
      unit: combines ? row.unit : supplement.unit,
    });
  });
  return out;
}

/**
 * A row after the member changes its dose: what the analysis said about the
 * OLD amount is dropped (Tanu, 2026-09-24).
 *
 * - `add_amount` / `check_amount` described the amount that was there. Kept
 *   after an edit, the notice quotes the new number under the old verdict —
 *   "1,000 mg is more than a daily dose could be" for a dose that is fine.
 * - `merged` said this amount was a sum of several entries. Once the member
 *   types their own number it is not.
 *
 * `not_in_library` stays: it is about the supplement, not the amount. Rows
 * with none of these fields — every stack that was never analysed — come back
 * unchanged, so this is safe on any stack.
 *
 * **Only a changed amount counts.** Given the row as it was, a commit that
 * leaves the dose and unit as they were changes nothing. Found in review
 * (2026-09-27): pressing ✓ on an unchanged "2 tablet" cleared `add_amount`,
 * the studied dose appeared beside a count, and — the signature being
 * unchanged — Save stayed enabled and stored the row without its prompt.
 * Compared as strings, the same way `analysisSignature` compares them.
 *
 * @param {object} row the supplement after the edit was applied
 * @param {object} [previous] the supplement before the edit
 * @returns {object} a new row; the input is not mutated
 */
export function afterDoseEdit(row, previous) {
  if (!row) return row;
  const same = (field) => String(row[field] ?? '') === String(previous?.[field] ?? '');
  if (previous && same('dosage') && same('unit')) return row;
  const next = { ...row };
  if (next.entryState === ENTRY_STATES.ADD_AMOUNT || next.entryState === ENTRY_STATES.CHECK_AMOUNT) {
    next.entryState = null;
  }
  if (next.merged === true) {
    next.merged = false;
    delete next.mergedEntries;
  }
  return next;
}

/**
 * Supplement ids present in the member's rows but absent from the analysis.
 *
 * The completion handler merges onto the rows the member has **now**, not the
 * rows the request was built from, because discarding edits made while waiting
 * would be worse than an incomplete analysis. The cost is that a supplement
 * added mid-run carries no results, and a row with no results looks exactly
 * like one the research had nothing to say about. This is what lets the form
 * say which it is.
 *
 * Ids the response *does* carry are not reported even when the member removed
 * them: a row that is gone needs no explanation.
 */
export function unanalyzedIds(supplements, analysis) {
  const analysed = new Set(
    (Array.isArray(analysis?.supplements) ? analysis.supplements : [])
      .map((row) => idKey(row?.supplementId))
      .filter(Boolean)
  );
  return (supplements || [])
    .filter((s) => s && idKey(s.supplementId) && s.selected !== false && !analysed.has(idKey(s.supplementId)))
    .map((s) => s.supplementId);
}

/**
 * The form a supplement id is matched on: trimmed, because the backend trims
 * ids and rows saved before the add dialog trimmed them still carry the space
 * ("creatine gummies " came back as "creatine gummies" and matched nothing —
 * Tanu's staging test, case C1). The row keeps the id it has; only matching
 * uses this.
 */
function idKey(id) {
  return id == null ? '' : String(id).trim();
}

/**
 * Which of the muted lines apply to a set of analysed rows.
 *
 * Read off the merged rows rather than the raw response so it keeps answering
 * correctly for a stack reopened from Firestore, where the response is long
 * gone and the rows are all there is. That is also the acceptance criterion:
 * "the saved stack stores results; reopening shows them".
 *
 * Also returns the display names each line applies to (Tanu, 2026-09-24: a
 * line that does not say which supplement it means leaves the member to guess),
 * de-duplicated and in list order.
 *
 * @returns {{hasMerged: boolean, hasNotInLibrary: boolean, hasNoStudies: boolean,
 *            mergedNames: string[], notInLibraryNames: string[], noStudiesNames: string[]}}
 */
export function analysisNoteFlags(supplements) {
  const rows = Array.isArray(supplements) ? supplements : [];
  const namesWhere = (test) => [
    ...new Set(rows.filter((s) => s && test(s)).map((s) => s.name || s.supplementId).filter(Boolean)),
  ];
  // "Combined" only where the amounts really were: a unit conflict leaves the
  // rows separate, and the line would then describe something that did not happen.
  const isCombined = (s) => s.merged === true && !s.unitConflict;
  const mergedNames = namesWhere(isCombined);
  const notInLibraryNames = namesWhere((s) => s.entryState === ENTRY_STATES.NOT_IN_LIBRARY);
  // Analysed and found nothing for this goal. A not-in-library row is left to
  // its own line, which already says why it has no studies, and a row the
  // member switched off was not part of the analysis.
  const noStudiesNames = namesWhere((s) => wasAnalysed(s)
    && s.study_count === 0
    && s.entryState !== ENTRY_STATES.NOT_IN_LIBRARY
    && s.selected !== false);
  return {
    hasMerged: rows.some((s) => s && isCombined(s)),
    hasNotInLibrary: rows.some((s) => s?.entryState === ENTRY_STATES.NOT_IN_LIBRARY),
    hasNoStudies: noStudiesNames.length > 0,
    mergedNames,
    notInLibraryNames,
    noStudiesNames,
  };
}

/**
 * Whether the analysis returned this row. `study_count: 0` alone does not say
 * that: the add dialog seeds it on every new row, so a supplement added after
 * (or during) an analysis would look analysed though it was never sent. Only a
 * row the analysis returned carries `studyDose` (null when there is none) or
 * an `entryState`.
 */
export function wasAnalysed(supplement) {
  return Boolean(supplement)
    && (supplement.entryState != null || supplement.studyDose !== undefined);
}

export const ROW_ANALYSIS_STATES = Object.freeze({
  ANALYZING: 'analyzing',
  NOT_ANALYZED: 'not_analyzed',
  NO_STUDIES: 'no_studies',
  NOT_IN_LIBRARY: 'not_in_library',
});

/**
 * The analysis state a row should say out loud, or `null` when its score ring
 * already says it.
 *
 * An analysed row with no studies and a row never analysed drew the same "—"
 * ring, so a member could not tell "the research has nothing on this" from
 * "this was never looked at" (2026-10-04). The ring keeps meaning one thing
 * — a score — and these name every other case:
 *
 * - `analyzing`: a run is in progress, for every row in it.
 * - `not_analyzed`: other rows have results and this one does not — added
 *   after the last run. Before any analysis there is nothing to contrast with,
 *   so no row is labelled.
 * - `not_in_library`, `no_studies`: analysed, and why there is no score.
 *
 * A row the member switched off is out of the analysis and says nothing.
 *
 * @param {object} supplement the row
 * @param {{analyzing?: boolean, listAnalysed?: boolean}} context
 * @returns {string|null} one of ROW_ANALYSIS_STATES, or null
 */
export function rowAnalysisState(supplement, { analyzing = false, listAnalysed = false } = {}) {
  if (!supplement || supplement.selected === false) return null;
  if (analyzing) return ROW_ANALYSIS_STATES.ANALYZING;
  if (!wasAnalysed(supplement)) {
    return listAnalysed ? ROW_ANALYSIS_STATES.NOT_ANALYZED : null;
  }
  if (supplement.entryState === ENTRY_STATES.NOT_IN_LIBRARY) return ROW_ANALYSIS_STATES.NOT_IN_LIBRARY;
  if (supplement.study_count === 0) return ROW_ANALYSIS_STATES.NO_STUDIES;
  return null;
}

/**
 * True when this stack carries analysis results at all — the switch that turns
 * the two-chip row on. Derived from the rows rather than from `origin` so a
 * stack analysed before `origin` was recorded still renders its results.
 */
export function hasAnalysis(supplements) {
  const rows = Array.isArray(supplements) ? supplements : [];
  return rows.some((s) => s && (s.studyDose != null || s.entryState != null));
}

/**
 * True when the analyze endpoint answered with a finished body instead of a
 * job to poll.
 *
 * **The endpoint has two response shapes and only one of them was known.** On
 * a cache miss the async decorator answers 202 with a `job_id` to poll; on a
 * completed cache hit it answers **200 with the finished body and no job id**.
 * The first implementation treated any missing `job_id` as a failure, so a
 * member re-analysing the same stack — the normal repeat case, not an edge one
 * — was told the analysis had failed while the finished answer sat in the
 * response. Found by a live sighted pass, not by any test, because every test
 * mocked the shape that was believed to be the only one.
 *
 * Both halves of the guard earn their place: a 200 that still carries a job id
 * means the job is the source of truth and merging a partial body would lose
 * the real result, and a 200 with no `supplements` is an error shape that must
 * fall through to the failure line rather than be merged as an empty analysis.
 *
 * Lives here, in the module that imports nothing, rather than beside the hook
 * that uses it — reaching it through the hook pulls in `axios` via the API
 * service, which Jest cannot transform.
 */
export function isSyncAnalysisPayload(status, data) {
  if (status !== 200 || !data || typeof data !== 'object') return false;
  if (data.job_id) return false;
  return Array.isArray(data.supplements);
}

/**
 * The request body for `/api/v2/analyze-stack`.
 *
 * Only `supplementId`, `dosage` and `unit` per row: the endpoint's sanitizer
 * strips anything else before the body is written to Supabase
 * `async_job_cache.input_params` (non-HIPAA), and a row's free-text `name` is
 * dropped there deliberately (PR 345). Sending only what will survive means
 * this side and that one cannot drift about what is stored.
 */
export function analysisRequestBody({ healthGoals, supplements, age, gender }) {
  return {
    healthGoals,
    age,
    gender,
    supplements: (supplements || [])
      .filter((s) => s && s.selected !== false && s.supplementId)
      .map((s) => ({
        supplementId: s.supplementId,
        dosage: s.dosage ?? null,
        unit: s.unit ?? null,
      })),
  };
}

/**
 * A fingerprint of what an analysis is *about*: the goal and, per row, the
 * `supplementId` / `dosage` / `unit` that `analysisRequestBody` would send.
 *
 * **Results are only current while this matches.** Everything the analysis
 * reads is in here, and nothing else is — renaming the stack, writing notes or
 * the analysis fields merged onto a row do not change it, so they do not make
 * results stale. Changing a dose (including through Add amount / Check
 * amount), adding or removing a supplement, or changing the goal does.
 *
 * Order-insensitive, and amounts compared as strings: the dose editor and the
 * add dialog do not agree on number vs string, and neither difference changes
 * what the analysis would say.
 */
export function analysisSignature(healthGoals, supplements) {
  const rows = analysisRequestBody({ healthGoals, supplements }).supplements
    .map((s) => [s.supplementId, String(s.dosage ?? ''), String(s.unit ?? '')].join('|'))
    .sort();
  return JSON.stringify({ goal: String(healthGoals || '').trim(), rows });
}

// ---------------------------------------------------------------------------
// The rows an in-flight analysis is about
// ---------------------------------------------------------------------------

/**
 * `sessionStorage` key holding the supplement rows a running analysis was
 * started with.
 *
 * **Why this exists.** The analysis response identifies supplements by
 * `supplementId` alone — no name, no metadata — so it is only meaningful
 * folded back onto the member's own rows. The job itself outlives the form:
 * `StackGenerationContext` polls app-wide, so a member can start an analysis,
 * navigate away, and come back to a remounted, empty form. Without the rows
 * kept somewhere the remount can reach, the completed result merges into
 * nothing and the member's list is gone.
 *
 * Session-scoped and keyed by job id, so it cannot be read back for a
 * different job, and it is cleared the moment the result is consumed.
 */
export const PENDING_ROWS_KEY = 'analyzeStackPendingRows';

/** Remember the rows and goal an analysis was started with. */
export function rememberPendingRows(jobId, healthGoals, rows) {
  if (!jobId) return;
  try {
    sessionStorage.setItem(
      PENDING_ROWS_KEY,
      JSON.stringify({ jobId, healthGoals: healthGoals || '', rows: rows || [] })
    );
  } catch (e) {
    // sessionStorage unavailable (private mode / quota). The in-mount path
    // does not need this at all, so a failure here costs only the remount
    // case — not worth surfacing to the member.
  }
}

/** The rows a given job was started with, or null. */
export function recallPendingRows(jobId) {
  if (!jobId) return null;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(PENDING_ROWS_KEY) || 'null');
    if (!parsed || parsed.jobId !== jobId) return null;
    return { healthGoals: parsed.healthGoals || '', rows: parsed.rows || [] };
  } catch (e) {
    return null;
  }
}

/** Drop the record — the result has been consumed, or the job is over. */
export function forgetPendingRows() {
  try {
    sessionStorage.removeItem(PENDING_ROWS_KEY);
  } catch (e) {
    // Nothing to do; a stale record is ignored by `recallPendingRows`'s own
    // job-id check rather than trusted.
  }
}
